/**
 * Showcase variant of in-app notification popup. Renders the broadcast
 * through the shared <ShowcaseModal/> — the same visual language as the
 * landing-page FeaturedModelBanner.
 *
 * Field mapping:
 *   notification.title          → ShowcaseModal title
 *   notification.description    → ShowcaseModal desc
 *   templateData.tag            → ShowcaseModal tag
 *   templateData.brandIcon      → ShowcaseModal brandIcon
 *   notification.actions[0]     → header CTA (label + onClick → action handler)
 *   notification.actions[1..]   → rendered as a row of action buttons
 *                                  below the gallery
 *   notification.image/videoUrls → photos[] (images first, then videos)
 */

import { useMemo } from 'react';
import { ShowcaseModal } from '../ShowcaseModal';
import type { ShowcasePhoto } from '../ShowcaseModal';
import type {
  InAppNotification,
  ShowcaseTemplateData,
} from '../../../types';
import { pickLocalized } from '../../../utils/localized';
import {
  useNotificationActionHandler,
  useNotificationLanguage,
  resolveActionLabel,
  actionEmoji,
  parseMediaUrls,
} from './useActionHandler';
import styles from './Showcase.module.css';

interface ShowcaseProps {
  notification: InAppNotification;
  onDismiss: () => void;
}

const Showcase = ({ notification, onDismiss }: ShowcaseProps) => {
  const handleAction = useNotificationActionHandler(onDismiss);
  const lang = useNotificationLanguage();

  const data = (notification.templateData ?? {}) as Partial<ShowcaseTemplateData>;
  const tag = pickLocalized(data.tag, lang) || 'NEW';
  const brandIcon = data.brandIcon;

  const photos = useMemo<ShowcasePhoto[]>(() => {
    // Resolution order:
    //   1. Per-language override for the user's language
    //   2. RU fallback in localizedMedia
    //   3. Legacy top-level imageUrls/videoUrls + Localized captions
    const lm = notification.localizedMedia;
    const localizedSet = lm?.[lang] || lm?.ru;

    let images: string[];
    let videos: string[];
    let captions: (string | undefined)[];

    if (localizedSet) {
      images = localizedSet.imageUrls || [];
      videos = localizedSet.videoUrls || [];
      captions = localizedSet.captions || [];
    } else {
      images = parseMediaUrls(notification.imageUrl, notification.imageUrls);
      videos = parseMediaUrls(notification.videoUrl, notification.videoUrls);
      const legacy = notification.captions || [];
      captions = legacy.map((c) => pickLocalized(c, lang));
    }

    // Captions are stored images-first then videos-second.
    const result: ShowcasePhoto[] = [];
    images.forEach((url, i) => {
      result.push({ url, type: 'image', caption: captions[i] });
    });
    videos.forEach((url, i) => {
      result.push({ url, type: 'video', caption: captions[images.length + i] });
    });
    return result;
  }, [
    notification.imageUrl,
    notification.imageUrls,
    notification.videoUrl,
    notification.videoUrls,
    notification.captions,
    notification.localizedMedia,
    lang,
  ]);

  const actions = notification.actions ?? [];
  const primaryAction = actions[0];
  const secondaryActions = actions.slice(1);

  // Admin-authored title/description may contain inline HTML (<b>, <a>, etc).
  // Match the legacy Simple popup which used dangerouslySetInnerHTML for the
  // same fields. Localized fields are resolved to the user's current language
  // first.
  const titleHtml = pickLocalized(notification.title, lang);
  const descHtml = pickLocalized(notification.description, lang);
  const titleNode = <span dangerouslySetInnerHTML={{ __html: titleHtml }} />;
  const descNode = descHtml
    ? <span dangerouslySetInnerHTML={{ __html: descHtml }} />
    : undefined;

  return (
    <ShowcaseModal
      tag={tag}
      title={titleNode}
      desc={descNode}
      brandIcon={brandIcon}
      ctaLabel={primaryAction ? resolveActionLabel(primaryAction, lang) : undefined}
      onCtaClick={primaryAction ? () => handleAction(primaryAction) : undefined}
      photos={photos}
      onClose={onDismiss}
    >
      {secondaryActions.length > 0 && (
        <div className={styles.secondaryActions}>
          {secondaryActions.map((action, i) => (
            <button
              key={i}
              type="button"
              className={styles.secondaryButton}
              onClick={() => handleAction(action)}
            >
              {actionEmoji(action.type)}
              {resolveActionLabel(action, lang)}
            </button>
          ))}
        </div>
      )}
    </ShowcaseModal>
  );
};

export default Showcase;
